/**
 * Plan 21: session-scoped level progress + the `?level=` boot override.
 *
 * The level flow (grill 2026-10-21): death on ANY level and a FINAL-level win reset the run to
 * level 1; a win with a following level stores current+1. Progress lives in sessionStorage —
 * the level survives reloads/CONTINUE jumps within the tab, and a fresh tab starts at L1
 * (duplicated tabs / session restore inherit a COPY of the session — accepted, plan 21 D4). The URL
 * never carries level STATE anymore: `?level=N` is an explicit override (automation + deep
 * links) that wins over storage only when parse-valid; jumps strip it (persistent store) or set
 * it to the target (degraded store, where the override doubles as the transport so CONTINUE can
 * never strand the player on L1 — plan-gate r1 P0-1).
 *
 * Storage can throw at any op (SecurityError on the sessionStorage getter, QuotaExceededError on
 * setItem — HTML spec): the store probes lazily and demotes STICKILY to a per-instance memory
 * fallback that always carries the latest written value. Parsing is pinned to `/^-?\d+$/` so
 * `Number('') === 0`-class traps fall through to the default instead of overriding storage
 * (r1 P1-3).
 */

export const LEVEL_STORAGE_KEY = 'jreb1d.level.v1';

/** The two ops the store needs — the shape of `sessionStorage` and of every test fake. */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export interface LevelStore {
  /** The stored level as an integer, or null when absent/garbage. NO clamp here — `max` lives at
   *  boot (`readBootLevelIndex`), so a stale 3 after a deploy shipping 2 levels boots L2 instead
   *  of indexing SHIPPED_LEVELS out of range (r1 P1-4). */
  read(): number | null;
  write(level1Based: number): void;
  /** False once ANY storage op has thrown (sticky) — from then on all ops run on memory. */
  readonly persistent: boolean;
}

/** `/^-?\d+$/` → Number → clamp(1, max); anything else → null (falls through to storage/default). */
export function sanitizeLevel(raw: string | null, max: number): number | null {
  if (raw === null || !/^-?\d+$/.test(raw)) return null;
  const n = Number(raw);
  if (!Number.isInteger(n)) return null; // unreachable behind the regex; belt-and-braces
  return Math.min(max, Math.max(1, n));
}

/**
 * `get` is injected for tests; the default touches the `sessionStorage` global LAZILY inside the
 * try — under node vitest the global is absent and the bare reference throws ReferenceError,
 * which is exactly the caught-and-demoted path (r1 P1-2). The memory fallback lives in this
 * closure (per instance, no cross-test pollution) and always holds the latest written value, so
 * a read after a failed write can never resurrect a stale readable value (r1 P1-1b).
 */
export function createSessionLevelStore(get: () => StorageLike = () => sessionStorage): LevelStore {
  let memory: number | null = null;
  let persistent = true;
  return {
    get persistent(): boolean {
      return persistent;
    },
    read(): number | null {
      if (!persistent) return memory;
      try {
        const raw = get().getItem(LEVEL_STORAGE_KEY);
        if (raw === null || !/^-?\d+$/.test(raw)) return null;
        const n = Number(raw);
        return Number.isInteger(n) ? n : null;
      } catch {
        persistent = false;
        return memory;
      }
    },
    write(level1Based: number): void {
      memory = level1Based; // memory always tracks the latest value, persistent or not
      if (!persistent) return;
      try {
        get().setItem(LEVEL_STORAGE_KEY, String(level1Based));
      } catch {
        persistent = false; // sticky demotion: no later op touches storage again
      }
    },
  };
}

/**
 * The boot rule, 0-based: `?level=` override (present + parse-valid, clamped) ?? stored level
 * (clamped) ?? 1. A present-but-garbage param falls through to storage BY THE PARSER —
 * out-of-range integers are not garbage, they clamp and win (matching the pre-plan-21 URL
 * clamp; only non-integer strings fall through).
 */
export function readBootLevelIndex(max: number, params: URLSearchParams, store: LevelStore): number {
  const override = sanitizeLevel(params.get('level'), max);
  if (override !== null) return override - 1;
  const stored = store.read();
  if (stored !== null) return Math.min(max, Math.max(1, stored)) - 1;
  return 0;
}

/** The end-state rule (1-based): lost → 1; won with a next level → current+1; final win → 1. */
export function nextStoredLevel(won: boolean, levelIndex0: number, max: number): number {
  if (!won) return 1;
  return levelIndex0 + 1 < max ? levelIndex0 + 2 : 1;
}

/**
 * Plan 21 impl-gate fold (opus P2-2): the restart button's ACTION — in place exactly when the
 * latch target IS the current level (an L1 death; the 1-level edge of a final win). Pure so the
 * parity with hud.endScreenRestartLabel (TRY AGAIN ⟺ in-place, on the reachable cells) can be
 * pinned by test instead of trusted from two inline closures.
 */
export function restartsInPlace(endTarget: number, levelIndex0: number): boolean {
  return endTarget === levelIndex0 + 1;
}

/**
 * The jump target URL (r1 P0-1): a persistent store STRIPS `level` (storage carries the target,
 * the address bar stays clean); a demoted store SETS `level=target` — the override doubles as
 * the transport, because the memory value dies with the page. Other params are preserved and no
 * history entry is added by the caller (`location.replace`); the query is rewritten ONLY when
 * `level` is present or being set, so an already-clean persistent jump returns an EQUAL href
 * (the caller takes `location.reload()` then — r2 P2-d).
 */
export function nextLevelHref(href: string, target: number, persistent: boolean): string {
  const u = new URL(href);
  if (persistent) {
    if (!u.searchParams.has('level')) return href;
    u.searchParams.delete('level');
  } else {
    u.searchParams.set('level', String(target));
  }
  return u.href;
}
